import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Zap, Trophy, X, Lock } from "lucide-react";
import client, { formatErr } from "../lib/api";
import { toast } from "sonner";
import Modal from "../components/Modal";

const HERO_BG =
    "https://static.prod-images.emergentagent.com/jobs/7f4b9c3e-a397-4c31-aa8d-d23a6c9c2724/images/82cb292d37e849a74c210b3230ecc7289a338a65279f9ba4253584e99c2c14bd.png";

export default function Splash() {
    const [open, setOpen] = useState(false);
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const checkInvite = async () => {
        if (!code.trim()) {
            toast.error("Hah. You need to be invited.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await client.post("/auth/check-invite", { code: code.trim() });
            toast.success("Invite accepted. Welcome.");
            navigate(`/register?invite=${encodeURIComponent(data.code)}`);
        } catch (e) {
            toast.error(formatErr(e) || "Invalid invite");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="app-shell min-h-screen relative overflow-hidden">
            <div
                className="absolute inset-0 opacity-50"
                style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/60 to-charcoal-900" />
            <div className="absolute inset-0 diag-stripes opacity-40" />

            <div className="relative z-10 flex flex-col h-full min-h-screen px-6 pt-12 pb-10">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="flex items-center justify-between"
                >
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-neon-orange flex items-center justify-center">
                            <Flame className="text-white" size={22} />
                        </div>
                        <span className="font-display font-black text-xl uppercase tracking-tight">
                            No<span className="text-neon-green">Excuse</span>
                        </span>
                    </div>
                    <span className="surface-1 px-2 py-1 label-cap text-[10px] text-neon-green flex items-center gap-1">
                        <Lock size={10} /> Invite Only
                    </span>
                </motion.div>

                <div className="flex-1 flex flex-col justify-end mt-32">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.7 }}
                    >
                        <span className="label-cap text-neon-green text-xs">Level Up. Or Stay Weak.</span>
                        <h1 className="font-display font-black text-6xl sm:text-7xl uppercase tracking-tighter leading-[0.85] mt-3">
                            Prove It,
                            <br />
                            <span className="neon-orange">Or Stay At</span>
                            <br />
                            Your <span className="neon-green">Level.</span>
                        </h1>
                        <p className="mt-6 text-zinc-400 text-base leading-relaxed max-w-sm">
                            Every rep counts. Every video is checked. No fake progress. No excuses.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.5 }}
                        className="mt-10 space-y-3"
                    >
                        <button
                            onClick={() => setOpen(true)}
                            data-testid="splash-cta-register"
                            className="btn-primary block text-center w-full py-4 text-lg"
                        >
                            Start The Challenge
                        </button>
                        <Link
                            to="/login"
                            data-testid="splash-cta-login"
                            className="block text-center w-full py-4 surface-2 font-display uppercase tracking-wider font-bold text-zinc-300 hover:text-white transition-colors"
                        >
                            I Already Train Here
                        </Link>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9 }}
                        className="grid grid-cols-3 gap-2 mt-8"
                    >
                        {[
                            { icon: Zap, label: "Timed Reps" },
                            { icon: Flame, label: "Streaks" },
                            { icon: Trophy, label: "Real XP" },
                        ].map((s) => (
                            <div key={s.label} className="surface-1 p-3 flex flex-col items-center text-center">
                                <s.icon className="text-neon-green mb-1" size={18} />
                                <span className="text-[10px] uppercase tracking-widest text-zinc-400">{s.label}</span>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </div>

            <Modal open={open} onClose={() => setOpen(false)} title="Hah." testid="invite-modal">
                <div className="text-zinc-400 text-sm">
                    <span className="text-neon-orange font-display font-bold uppercase">You need to be invited.</span>{" "}
                    NoExcuse is invite-only. Got a code from your trainer or a friend? Drop it in.
                </div>
                <input
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="NX-XXXXXXXX"
                    data-testid="invite-input"
                    className="w-full mt-4 bg-charcoal-700 border border-charcoal-500 px-4 py-3 text-white font-mono tracking-widest text-center focus:border-neon-green focus:outline-none"
                />
                <button
                    onClick={checkInvite}
                    disabled={loading}
                    data-testid="invite-submit"
                    className="btn-neon w-full py-3 mt-3 text-base disabled:opacity-50"
                >
                    {loading ? "Checking..." : "Validate Invite"}
                </button>
                <button
                    onClick={() => setOpen(false)}
                    className="w-full py-2 mt-2 text-zinc-500 text-xs label-cap hover:text-white"
                >
                    No Code? Stay Weak.
                </button>
            </Modal>
        </div>
    );
}
